// Schedules (2026-10-13; approved mockup: the "FriSo Schedules" canvas artifact). A schedule is a repeating Busy
// event (repeat.js) with schedule: true — e.g. work, weekdays 9–5 — that pods see as "Busy". Extra fields on the
// events/ doc: paused (shows nowhere while true), futurePods (pods you create or join later get it too) and
// syncGoogle (keep a repeating copy in the FriSo Google calendar). The name is private like any Busy title
// (eventPrivate). Display only: Calendar.js does all saving (saveSchedule / toggleSchedulePause / deleteSchedule).
import { useState } from "react";
import { createPortal } from "react-dom";
import { formatTime } from "./dateUtils";
import { repeatLabel } from "./repeat";
import { getPodColor } from "./podColors";
import { PickerField, TimeRange } from "./EventForm";

const DAYS = [[1, "M", "Monday"], [2, "T", "Tuesday"], [3, "W", "Wednesday"], [4, "T", "Thursday"], [5, "F", "Friday"], [6, "S", "Saturday"], [0, "S", "Sunday"]];
const PRESETS = [["Weekdays", [1, 2, 3, 4, 5]], ["Weekends", [0, 6]], ["Every day", [0, 1, 2, 3, 4, 5, 6]]];

export const scheduleWhen = (s) =>
  `${repeatLabel({ ...s.repeat, until: null }, s.startDate)} ${s.startTime ? `${formatTime(s.startTime)}${s.endTime ? `–${formatTime(s.endTime)}` : ""}` : "all day"}`;

// Dashboard card: one line per schedule with an on/off (paused) switch; tapping a row edits it.
export function SchedulesCard({ schedules, onAdd, onEdit, onTogglePause }) {
  return (
    <section className="card dash-sched" aria-label="Schedules">
      <div className="dash-card-head">
        <h2>Schedules</h2>
        <button type="button" className="btn btn--secondary btn--sm" onClick={onAdd}>+ Add</button>
      </div>
      {schedules.length === 0 && (
        <p className="dash-muted">Set your usual busy times, like work or class, so your pods know when you're free.</p>
      )}
      {schedules.map((s) => (
        <div key={s.id} className={`dash-sched__row${s.paused ? " is-paused" : ""}`}>
          <button type="button" className="dash-sched__open" onClick={() => onEdit(s)}>
            <b>{s.title || "Busy"}</b> <span>· {scheduleWhen(s)}</span>
          </button>
          <button type="button" className="sched-switch" role="switch" aria-checked={!s.paused}
            aria-label={`${s.title || "Schedule"}: ${s.paused ? "paused" : "on"}`} onClick={() => onTogglePause(s)}><i /></button>
        </div>
      ))}
    </section>
  );
}

// New / edit schedule. `schedule` is {} for a new one, else its events/ doc (with the private name overlaid).
export function ScheduleForm({ schedule, pods, canGoogle, googleOn, onSave, onDelete, onClose }) {
  const isNew = !schedule.id;
  const podList = Object.values(pods);
  const [name, setName] = useState(schedule.title && schedule.title !== "Busy" ? schedule.title : "");
  const [days, setDays] = useState(schedule.repeat?.days?.length ? schedule.repeat.days : [1, 2, 3, 4, 5]);
  const [allDay, setAllDay] = useState(!isNew && !schedule.startTime);
  const [startTime, setStartTime] = useState(schedule.startTime || "09:00");
  const [endTime, setEndTime] = useState(schedule.endTime || "17:00");
  const [until, setUntil] = useState(schedule.repeat?.until || null);
  const [podIds, setPodIds] = useState(isNew ? podList.map((p) => p.id) : (schedule.sharedPods || []).filter((id) => pods[id]));
  const [futurePods, setFuturePods] = useState(isNew ? true : !!schedule.futurePods);
  const [syncGoogle, setSyncGoogle] = useState(isNew ? false : googleOn);
  const [busy, setBusy] = useState(false);
  const today = new Date();
  const todayKey = [today.getFullYear(), String(today.getMonth() + 1).padStart(2, "0"), String(today.getDate()).padStart(2, "0")].join("-");
  const ok = name.trim() && days.length && (allDay || (startTime && endTime && endTime > startTime));

  const save = async () => {
    if (!ok || busy) return;
    setBusy(true);
    try {
      await onSave({ name: name.trim(), days, allDay, startTime, endTime, until, podIds, futurePods, syncGoogle });
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div className="modal-overlay" onClick={onClose} onTouchMove={(e) => e.stopPropagation()}>
      <div className="modal sched-form" role="dialog" aria-label={isNew ? "New schedule" : "Edit schedule"} onClick={(e) => e.stopPropagation()}>
        <div className="modal__header">
          <div className="dash-eyebrow">{isNew ? "New schedule" : "Schedule"}</div>
          <button type="button" className="btn btn--ghost btn--sm" aria-label="Close" onClick={onClose}>✕</button>
        </div>
        <div className="modal__content sched-form__body">
          <label className="sched-form__name">
            <input className="ef-title" aria-label="Name" value={name} maxLength={80} placeholder="What is it? e.g. Work" autoFocus={isNew && window.innerWidth > 640}
              onChange={(e) => setName(e.target.value)} />
            <small>Only you see this name. Your pods see “Busy”.</small>
          </label>

          <div className="ef-sec">
            <div className="ef-label">Repeats on</div>
            <div className="ef-repeat__row" role="group" aria-label="Days">
              {DAYS.map(([d, short, long]) => (
                <button key={d} type="button" className="ef-day" aria-pressed={days.includes(d)} aria-label={long}
                  onClick={() => setDays(days.includes(d) ? days.filter((x) => x !== d) : [...days, d])}>{short}</button>
              ))}
            </div>
            <div className="ef-repeat__row">
              {PRESETS.map(([label, set]) => (
                <button key={label} type="button" className="ef-chip" aria-pressed={days.length === set.length && set.every((x) => days.includes(x))} onClick={() => setDays(set)}>{label}</button>
              ))}
            </div>
          </div>

          <div className="ef-sec">
            <div className="ef-label">Time</div>
            <div className="ef-when">
              {!allDay && (
                <TimeRange startTime={startTime} endTime={endTime}
                  onChange={(p) => { if ("startTime" in p) setStartTime(p.startTime); if ("endTime" in p) setEndTime(p.endTime); }} />
              )}
              <button type="button" className="ef-switch" role="switch" aria-checked={allDay} onClick={() => setAllDay(!allDay)}><i />All day</button>
            </div>
            {!allDay && startTime && endTime && endTime <= startTime && <div className="ef-hint">The end time needs to be after the start.</div>}
            <div className="ef-repeat__row">
              <span className="ef-hint">{isNew ? "Starts today" : "Repeating"} ·</span>
              {until ? (
                <>
                  <span className="ef-hint">ends</span>
                  <PickerField type="date" className="ef-field--date" label="Last date" value={until} min={todayKey} onChange={(v) => v && setUntil(v)} />
                  <button type="button" className="ef-link" onClick={() => setUntil(null)}>No end</button>
                </>
              ) : (
                <button type="button" className="ef-link" onClick={() => {
                  const d = new Date(); d.setMonth(d.getMonth() + 3);
                  setUntil([d.getFullYear(), String(d.getMonth() + 1).padStart(2, "0"), String(d.getDate()).padStart(2, "0")].join("-"));
                }}>Set an end date</button>
              )}
            </div>
          </div>

          <div className="ef-sec">
            <div className="ef-label">Show as Busy to</div>
            {podList.length === 0 ? (
              <div className="ef-hint">You're not in any pods yet. Pods you make or join will see it if the switch below is on.</div>
            ) : (
              <div className="ef-pods">
                {podList.map((p) => {
                  const on = podIds.includes(p.id);
                  const c = getPodColor(p.id);
                  return (
                    <button key={p.id} type="button" className="ef-pod" aria-pressed={on}
                      style={on ? { background: c.bg, color: c.text, borderColor: c.border } : undefined}
                      onClick={() => setPodIds(on ? podIds.filter((x) => x !== p.id) : [...podIds, p.id])}>
                      <i style={{ background: c.border }} />{p.name}
                    </button>
                  );
                })}
              </div>
            )}
            <button type="button" className="sched-future" role="switch" aria-checked={futurePods} onClick={() => setFuturePods(!futurePods)}>
              <span><b>Share to pods I join or create later</b><small>New pods see this as Busy without you adding it.</small></span>
              <span className="sched-switch" aria-hidden="true"><i /></span>
            </button>
          </div>
        </div>
        <div className="ef-foot sched-form__foot">
          {canGoogle && (
            <button type="button" className="ef-switch" role="switch" aria-checked={syncGoogle} onClick={() => setSyncGoogle(!syncGoogle)}><i />Add to Google Calendar</button>
          )}
          <span className="sched-form__actions">
            {!isNew && <button type="button" className="btn btn--ghost pg-btn pg-danger" onClick={onDelete}>Delete</button>}
            <button type="button" className="btn btn--primary pg-btn" disabled={!ok || busy} onClick={save}>{busy ? "Saving…" : isNew ? "Save schedule" : "Save"}</button>
          </span>
        </div>
      </div>
    </div>,
    document.querySelector(".app-shell") || document.body
  );
}
